import React from "react";
import { Link } from "react-router-dom";
import "./styles/MaritimeSystems.css";

const commsBlocks = [
  {
    title: "Satellite communications",
    text: "Satellite connectivity helps vessels stay in touch when sailing beyond coastal coverage, supporting voice, data, and operational traffic at sea.",
  },
  {
    title: "Two way radio services",
    text: "Marine radio solutions provide clear, reliable communication between crew, nearby vessels, and port operations within radio coverage areas.",
  },
  {
    title: "Mobile communications",
    text: "Mobile and onboard wireless options help support crew communication, operational updates, and day to day coordination.",
  },
];

const safetyBlocks = [
  {
    title: "Security video cameras",
    text: "Marine focused video systems help monitor decks, engine rooms, and critical zones to support safety and situational awareness.",
  },
  {
    title: "Fire alarm systems",
    text: "Fire detection and alarm systems give crews early warning and support a more structured response in case of onboard incidents.",
  },
  {
    title: "Onboard monitoring",
    text: "Integrated monitoring helps teams see more of what is happening across the vessel, from key spaces to access points.",
  },
];

const comfortBlocks = [
  {
    title: "Satellite TV systems",
    text: "Onboard satellite TV solutions support crew welfare and provide entertainment during time off at sea.",
  },
  {
    title: "Connected services",
    text: "Connectivity and communication tools help crews stay in touch with shore, access digital services, and receive important updates.",
  },
];

const outcomeCards = [
  {
    title: "More dependable communication at sea",
    text: "Combine satellite, radio, and mobile options so crews can stay connected across more routes and operating conditions.",
  },
  {
    title: "Stronger onboard safety",
    text: "Use security cameras and fire alarm systems to support earlier detection and clearer visibility into vessel conditions.",
  },
  {
    title: "Better crew experience",
    text: "Support crew with communication tools and entertainment systems that help day to day life onboard.",
  },
];

const fitCards = [
  {
    title: "Commercial and cargo vessels",
    text: "For operators that need dependable connectivity and safety systems across longer routes and mixed operating conditions.",
  },
  {
    title: "Offshore and support vessels",
    text: "For vessels supporting offshore work that rely on constant communication and monitoring for operations and safety.",
  },
  {
    title: "Passenger and crew focused ships",
    text: "For vessels where both safety and onboard experience are important for crew, staff, or passengers.",
  },
];

export default function MaritimeSystems() {
  return (
    <div className="maritimePage">
      <section className="maritimeHero">
        <div className="maritimeContainer maritimeHero__container">
          <div className="maritimeHero__content">
            <p className="maritimeEyebrow">Maritime Systems</p>

            <h1 className="maritimeHero__title">
              Marine communications, safety, and onboard experience for vessels at sea.
            </h1>

            <p className="maritimeHero__text">
              Our maritime systems combine satellite wireless communications,
              two way radio services, security video cameras, mobile
              communications, satellite TV systems, and fire alarm solutions.
              The goal is to help vessels stay connected, support safety, and
              improve life onboard for crew and operations.
            </p>

            <div className="maritimeHero__actions">
              <Link to="/contact" className="maritimeBtn maritimeBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="maritimeBtn maritimeBtn--ghost">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="maritimeHero__visual">
            <div className="maritimeScene">
              <div className="maritimeScene__sea" />
              <div className="maritimeShip">
                <div className="maritimeShip__deck" />
                <div className="maritimeShip__bridge">
                  <span className="maritimeTag maritimeTag--sat">Satellite</span>
                </div>
                <div className="maritimeShip__mast">
                  <span className="maritimeTag maritimeTag--radio">Radio</span>
                </div>
                <div className="maritimeShip__cabin">
                  <span className="maritimeTag maritimeTag--safety">Safety</span>
                </div>
              </div>

              <div className="maritimeWave maritimeWave--one" />
              <div className="maritimeWave maritimeWave--two" />

              <div className="maritimeSat">
                <span className="maritimeSat__body" />
                <span className="maritimeSat__beam" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="maritimeCommSection">
        <div className="maritimeContainer">
          <div className="maritimeSectionHead maritimeSectionHead--center">
            <p className="maritimeEyebrow">Connectivity at sea</p>
            <h2 className="maritimeTitle">
              Communication options designed around vessel operations
            </h2>
            <p className="maritimeText">
              Marine communication systems help crews stay in contact with shore,
              nearby vessels, and onboard teams across different sailing
              conditions.
            </p>
          </div>

          <div className="maritimeCommGrid">
            {commsBlocks.map((item) => (
              <article key={item.title} className="maritimeCommCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="maritimeSafetySection">
  <div className="maritimeContainer maritimeSafetyWrap">
    <div className="maritimeSafetyIntro">
      <p className="maritimeEyebrow">Onboard safety and monitoring</p>
      <h2 className="maritimeTitle">
        Systems that help crews see more and respond faster
      </h2>
      <p className="maritimeText">
        Security video cameras and fire alarm systems add important
        visibility and alerting capabilities for ships and offshore
        environments.
      </p>
    </div>

    <div className="maritimeSafetyGrid">
      {safetyBlocks.map((item) => (
        <article key={item.title} className="maritimeSafetyCard">
          <h3>{item.title}</h3>
          <p>{item.text}</p>
        </article>
      ))}
    </div>
  </div>
</section>


      <section className="maritimeComfortSection">
        <div className="maritimeContainer">
          <div className="maritimeComfortBand">
            <div className="maritimeComfortBand__intro">
              <p className="maritimeEyebrow">Crew experience</p>
              <h2 className="maritimeTitle">
                Supporting life onboard with communication and entertainment
              </h2>
              <p className="maritimeText">
                Strong connectivity and onboard systems help support crew
                welfare, information access, and day to day life at sea.
              </p>
            </div>

            <div className="maritimeComfortGrid">
              {comfortBlocks.map((item) => (
                <article key={item.title} className="maritimeComfortCard">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="maritimeOutcomesSection">
        <div className="maritimeContainer">
          <div className="maritimeSectionHead">
            <p className="maritimeEyebrow">Operational outcomes</p>
            <h2 className="maritimeTitle">
              What an integrated maritime systems approach helps improve
            </h2>
            <p className="maritimeText">
              The focus is on helping vessels stay connected, improving onboard
              safety, and supporting better crew conditions across journeys.
            </p>
          </div>

          <div className="maritimeOutcomeGrid">
            {outcomeCards.map((item) => (
              <article key={item.title} className="maritimeOutcomeCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="maritimeFitSection">
        <div className="maritimeContainer">
          <div className="maritimeSectionHead maritimeSectionHead--center">
            <p className="maritimeEyebrow">Best fit</p>
            <h2 className="maritimeTitle">
              Where Maritime Systems create the most value
            </h2>
            <p className="maritimeText">
              These services support vessels and projects where connectivity,
              safety, and crew experience all matter to daily operations.
            </p>
          </div>

          <div className="maritimeFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="maritimeFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="maritimeCta">
        <div className="maritimeContainer maritimeCta__container">
          <div>
            <p className="maritimeEyebrow maritimeEyebrow--light">Next step</p>
            <h2 className="maritimeCta__title">
              Plan marine communication and safety systems for your fleet
            </h2>
            <p className="maritimeCta__text">
              If you are exploring satellite and radio communications, onboard
              safety monitoring, or crew experience systems for marine
              operations, ComNetworks.net can help design a solution that fits
              your vessels and routes.
            </p>
          </div>

          <div className="maritimeCta__actions">
            <Link to="/contact" className="maritimeBtn maritimeBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="maritimeBtn maritimeBtn--ghostLight">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
